/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types'
import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'

import {
    Button,
    Flex,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverFooter,
    PopoverHeader,
    PopoverTrigger,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {ChevronDownIcon} from '../../icons'
import Link from '../../link'

const AccountDropdown = ({
    isOpen,
    onClose,
    onOpen,
    handleIconsMouseLeave,
    hasEnterPopoverContent,
    keyMap,
    onSignoutClick,
    styles,
    dropdownData
}) => {
    const intl = useIntl()

    const title = useMemo(() => {
        if (dropdownData?.title) return dropdownData?.title

        return intl.formatMessage({
            defaultMessage: 'My account',
            id: 'header.popover.title.my_account'
        })
    }, [dropdownData])

    return (
        <Popover
            isLazy
            arrowSize="none"
            isOpen={isOpen}
            placement="bottom-end"
            onClose={onClose}
            onOpen={onOpen}
            offset={[-40, 16]}
        >
            <PopoverTrigger>
                <ChevronDownIcon
                    aria-label="My account trigger"
                    onMouseLeave={handleIconsMouseLeave}
                    onKeyDown={(e) => {
                        keyMap[e.key]?.(e)
                    }}
                    {...styles.arrowDown}
                    onMouseOver={onOpen}
                    tabIndex={0}
                />
            </PopoverTrigger>

            <PopoverContent
                rootProps={{
                    borderRadius: '12px'
                }}
                borderRadius="12px"
                _focus={{boxShadow: 'none'}}
                visibility="visible !important"
                onMouseLeave={() => {
                    hasEnterPopoverContent.current = false
                    onClose()
                }}
                onMouseOver={() => {
                    hasEnterPopoverContent.current = true
                }}
            >
                <PopoverArrow />
                <PopoverHeader
                    paddingBottom="40px"
                    paddingTop="40px"
                    paddingLeft="48px"
                >
                    <Text fontFamily="Raleway" fontSize="24px" fontWeight="600">
                        {title}
                    </Text>
                </PopoverHeader>
                <PopoverBody paddingLeft="48px">
                    <Stack
                        spacing={4}
                        as="nav"
                        data-testid="account-detail-nav"
                    >
                        {dropdownData?.links?.map((link, index) => (
                            <Link
                                {...link}
                                key={`${link.contentfulTitle}-${index}`}
                                variant="menu-link"
                                buttonStyles={{
                                    display: 'flex',
                                    flexFlow: 'row-reverse',
                                    alignItems: 'center',
                                    justifyContent: 'left',
                                    gap: '16px',
                                    fontWeight: '400',
                                    fontSize: '16px',
                                    fontFamily: 'Roboto'
                                }}
                            />
                        ))}
                    </Stack>
                </PopoverBody>
                <PopoverFooter
                    onClick={onSignoutClick}
                    cursor="pointer"
                    paddingLeft="48px"
                    paddingTop="40px"
                    paddingBottom="40px"
                >
                    <Button
                        variant="unstyled"
                        padding="0"
                        margin="0"
                        height="fit-content"
                    >
                        <Flex>
                            <Text
                                as="span"
                                fontSize="14px"
                                fontWeight="400"
                                fontFamily="Roboto"
                                color="#707070"
                            >
                                {intl.formatMessage({
                                    defaultMessage: 'Log out',
                                    id: 'header.popover.action.log_out'
                                })}
                            </Text>
                        </Flex>
                    </Button>
                </PopoverFooter>
            </PopoverContent>
        </Popover>
    )
}

AccountDropdown.propTypes = {
    dropdownData: PropTypes.object,
    isOpen: PropTypes.bool,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    handleIconsMouseLeave: PropTypes.func,
    keyMap: PropTypes.object,
    hasEnterPopoverContent: PropTypes.object,
    onSignoutClick: PropTypes.func,
    styles: PropTypes.shape({
        signoutIcon: PropTypes.object,
        signoutText: PropTypes.object,
        signout: PropTypes.object,
        popoverContent: PropTypes.object,
        arrowDown: PropTypes.object // Add this line
    })
}

export default AccountDropdown
